import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { motion } from 'framer-motion';
import {
  Alert,
  Box,
  TextField,
  Button,
  CircularProgress,
  Switch,
  FormControlLabel,
  Typography,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
  Chip,
  Card,
  CardContent,
  MenuItem,
} from '@mui/material';
import {
  Business,
  Email,
  Phone,
  Person,
  Security,
  Settings,
  SmartToy,
  Description,
  Visibility,
  VisibilityOff,
  Save,
  Cancel,
  Info,
  Campaign,
} from '@mui/icons-material';
import { adminApi } from '../../api/adminApi';

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function AdminClientForm({
  onSubmit,
  defaultValues = {},
  isEdit = false,
  saving = false,
  onCancel,
  clientId = null,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [testingOurSms, setTestingOurSms] = useState(false);
  const [ourSmsTestResult, setOurSmsTestResult] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
  } = useForm({
    defaultValues: {
      company_name: '',
      email: '',
      provider: 'gupshup',
      gupshup_phone_number: '',
      gupshup_app_name: '',
      gupshup_api_key: '',
      gupshup_api_url: 'https://api.gupshup.io/wa/api/v1/msg',
      meta_phone_number_id: '',
      meta_business_account_id: '',
      meta_api_version: 'v20.0',
      meta_access_token: '',
      ycloud_api_key: '',
      ycloud_api_secret: '',
      ycloud_channel_id: '',
      ycloud_phone_number: '',
      ycloud_api_url: 'https://api.ycloud.com/v2/whatsapp/messages/sendDirectly',
      sms_enabled: false,
      oursms_username: '',
      oursms_auth_mode: 'basic',
      oursms_password: '',
      oursms_api_token: '',
      oursms_sender_id: '',
      oursms_api_base_url: 'https://api.oursms.com/api-a',
      sms_timezone: 'Asia/Riyadh',
      ai_prompt: '',
      knowledge_base_text: '',
      username: '',
      password: '',
      is_active: true,
      ...defaultValues,
    },
  });

  const watchedValues = watch();
  const selectedProvider = watchedValues.provider || 'gupshup';

  const handleTogglePasswordVisibility = () => {
    setShowPassword((current) => !current);
  };

  const handleTestOurSms = async () => {
    if (!clientId) {
      return;
    }

    setTestingOurSms(true);
    setOurSmsTestResult(null);

    try {
      const values = getValues();
      const payload = {
        oursms_username: values.oursms_username,
        oursms_auth_mode: values.oursms_auth_mode,
        oursms_password: values.oursms_password,
        oursms_api_token: values.oursms_api_token,
        oursms_api_base_url: values.oursms_api_base_url,
        oursms_sender_id: values.oursms_sender_id,
      };
      const result = await adminApi.testOurSmsConnection(clientId, payload);
      setOurSmsTestResult({
        severity: 'success',
        message: result.message || 'OurSMS credentials are valid.',
        details: result.response ? JSON.stringify(result.response, null, 2) : '',
      });
    } catch (error) {
      setOurSmsTestResult({
        severity: 'error',
        message: error.message || 'Failed to test OurSMS credentials.',
        details: '',
      });
    } finally {
      setTestingOurSms(false);
    }
  };

  const SectionHeader = ({ icon, title, subtitle, color = 'primary' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <Box
        sx={{
          p: 1,
          bgcolor: `${color}.50`,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" fontWeight="600" color={`${color}.main`}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const FormField = ({ name, label, rules, type = 'text', multiline = false, rows = 1, children, ...props }) => (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <TextField
          {...field}
          {...props}
          select={Boolean(props.select)}
          label={label}
          type={type}
          multiline={multiline}
          rows={rows}
          fullWidth
          error={!!errors[name]}
          helperText={errors[name]?.message || props.helperText}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
          }}
        >
          {children}
        </TextField>
      )}
    />
  );

  const providerLabelMap = {
    gupshup: 'Gupshup',
    meta: 'Meta Cloud',
    ycloud: 'YCloud',
  };

  const providerPrimaryValue =
    selectedProvider === 'gupshup'
      ? watchedValues.gupshup_phone_number
      : selectedProvider === 'meta'
        ? watchedValues.meta_phone_number_id
        : watchedValues.ycloud_phone_number;

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      variants={formVariants}
      initial="hidden"
      animate="visible"
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader
            icon={<Business color="primary" />}
            title="Basic Information"
            subtitle="Company data and WhatsApp provider selection"
            color="primary"
          />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormField
                name="company_name"
                label="Company Name"
                rules={{ required: 'Company name is required.' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                name="email"
                label="Email"
                type="email"
                rules={{
                  required: 'Email is required.',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email format.',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField name="provider" label="WhatsApp Provider" select>
                <MenuItem value="gupshup">Gupshup</MenuItem>
                <MenuItem value="meta">Meta Cloud</MenuItem>
                <MenuItem value="ycloud">YCloud</MenuItem>
              </FormField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={Boolean(field.value)} color="success" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">تشغيل البوت (الرد الآلي)</Typography>
                        <Chip
                          label={field.value ? 'البوت يعمل' : 'البوت متوقف'}
                          color={field.value ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedProvider === 'gupshup' && (
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader
              icon={<Settings color="primary" />}
              title="Gupshup Settings"
              subtitle="Shown only when Gupshup is selected"
              color="primary"
            />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormField
                  name="gupshup_phone_number"
                  label="Gupshup Phone Number"
                  rules={{ required: 'Phone number is required.' }}
                  placeholder="+966501234567"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField
                  name="gupshup_app_name"
                  label="Gupshup App Name"
                  rules={{ required: 'App name is required.' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField
                  name="gupshup_api_key"
                  label="Gupshup API Key"
                  type={showPassword ? 'text' : 'password'}
                  rules={{ required: 'API key is required.' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Security color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField
                  name="gupshup_api_url"
                  label="Gupshup API URL"
                  placeholder="https://api.gupshup.io/wa/api/v1/msg"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedProvider === 'meta' && (
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader
              icon={<Settings color="info" />}
              title="Meta Cloud Settings"
              subtitle="Shown only when Meta Cloud is selected"
              color="info"
            />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormField
                  name="meta_phone_number_id"
                  label="Meta Phone Number ID"
                  rules={{ required: 'Meta Phone Number ID is required.' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField name="meta_business_account_id" label="Meta Business Account ID" />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField name="meta_api_version" label="Meta API Version" />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField
                  name="meta_access_token"
                  label="Meta Access Token"
                  type={showPassword ? 'text' : 'password'}
                  rules={{ required: 'Meta Access Token is required.' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {selectedProvider === 'ycloud' && (
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader
              icon={<Settings color="secondary" />}
              title="YCloud Settings"
              subtitle="Shown only when YCloud is selected"
              color="secondary"
            />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormField
                  name="ycloud_api_key"
                  label="YCloud API Key"
                  rules={{ required: 'YCloud API Key is required.' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormField
                  name="ycloud_api_secret"
                  label="YCloud API Secret"
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormField name="ycloud_channel_id" label="YCloud Channel ID" />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormField
                  name="ycloud_phone_number"
                  label="YCloud Phone Number"
                  rules={{ required: 'YCloud phone number is required.' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormField name="ycloud_api_url" label="YCloud API URL" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader
            icon={<Campaign color="success" />}
            title="OurSMS Settings"
            subtitle="SMS gateway credentials for campaigns"
            color="success"
          />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="sms_enabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={Boolean(field.value)} color="success" />}
                    label="Enable SMS Campaigns"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                name="oursms_username"
                label="OurSMS Username"
                disabled={!watchedValues.sms_enabled}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                name="oursms_auth_mode"
                label="OurSMS Auth Mode"
                select
                disabled={!watchedValues.sms_enabled}
              >
                <MenuItem value="basic">Username / Password</MenuItem>
                <MenuItem value="token">API Token</MenuItem>
              </FormField>
            </Grid>

            {watchedValues.oursms_auth_mode !== 'token' && (
              <Grid item xs={12} md={6}>
              <FormField
                name="oursms_password"
                label="OurSMS Password"
                type={showPassword ? 'text' : 'password'}
                disabled={!watchedValues.sms_enabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              </Grid>
            )}

            {watchedValues.oursms_auth_mode === 'token' && (
              <Grid item xs={12} md={6}>
                <FormField
                  name="oursms_api_token"
                  label="OurSMS API Token"
                  type={showPassword ? 'text' : 'password'}
                  disabled={!watchedValues.sms_enabled}
                  helperText={isEdit ? 'Leave blank to keep the current API token.' : ''}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePasswordVisibility} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <FormField
                name="oursms_sender_id"
                label="Sender ID"
                disabled={!watchedValues.sms_enabled}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField
                name="sms_timezone"
                label="Timezone"
                disabled={!watchedValues.sms_enabled}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField
                name="oursms_api_base_url"
                label="Base URL"
                disabled={!watchedValues.sms_enabled}
              />
            </Grid>

            {isEdit && clientId && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={handleTestOurSms}
                    disabled={!watchedValues.sms_enabled || testingOurSms}
                  >
                    {testingOurSms ? 'Testing OurSMS...' : 'Test OurSMS Connection'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Runs from the server using the values currently shown in this form.
                  </Typography>
                </Box>
              </Grid>
            )}

            {ourSmsTestResult && (
              <Grid item xs={12}>
                <Alert severity={ourSmsTestResult.severity}>
                  <Box>
                    <Typography variant="body2" fontWeight="600">
                      {ourSmsTestResult.message}
                    </Typography>
                    {ourSmsTestResult.details && (
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{ mt: 1, mb: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                      >
                        {ourSmsTestResult.details}
                      </Typography>
                    )}
                  </Box>
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader
            icon={<Person color="info" />}
            title="User Account"
            subtitle="Dashboard login credentials"
            color="info"
          />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormField
                name="username"
                label="Username"
                rules={{ required: !isEdit && 'Username is required.' }}
                disabled={isEdit}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                helperText={isEdit ? 'Username cannot be changed after creation.' : ''}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormField
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                rules={{ required: !isEdit && 'Password is required.' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Security color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={isEdit ? 'Leave blank to keep the current password.' : ''}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader
            icon={<SmartToy color="warning" />}
            title="Bot Settings"
            subtitle="Bot prompt and knowledge base"
            color="warning"
          />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField
                name="ai_prompt"
                label="Bot Prompt"
                multiline
                rows={4}
                placeholder="Describe the bot behavior and response style"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <SmartToy color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormField
                name="knowledge_base_text"
                label="Additional Knowledge"
                multiline
                rows={4}
                placeholder="Add operational notes or common questions"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                      <Description color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Info color="info" />
          <Typography variant="h6" fontWeight="600" color="info.main">
            Change Summary
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Company:</strong> {watchedValues.company_name || 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Email:</strong> {watchedValues.email || 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>WhatsApp Provider:</strong> {providerLabelMap[selectedProvider] || selectedProvider}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Primary Identifier:</strong> {providerPrimaryValue || 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>SMS Campaigns:</strong> {watchedValues.sms_enabled ? 'Enabled' : 'Disabled'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>OurSMS Auth:</strong> {watchedValues.oursms_auth_mode === 'token' ? 'API Token' : 'Username / Password'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Sender ID:</strong> {watchedValues.oursms_sender_id || 'Not set'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>الرد الآلي:</strong>{' '}
              <Chip
                label={watchedValues.is_active ? 'البوت يعمل' : 'البوت متوقف'}
                color={watchedValues.is_active ? 'success' : 'error'}
                size="small"
              />
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Button variant="outlined" startIcon={<Cancel />} onClick={onCancel} disabled={saving} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          disabled={saving}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #0ea5e9, #3b82f6)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0284c7, #2563eb)',
            },
          }}
        >
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Client'}
        </Button>
      </Box>
    </motion.form>
  );
}
